<template>
  <div class="workoutBasicForm">
    <div class="workoutMetaStrip workoutMetaStrip--action workoutMetaStrip--hasFootTip">
      <el-form-item :label="L.displayName" required class="workoutFormItem--displayName">
        <el-input v-model="local.display_name" />
      </el-form-item>
      <el-form-item :label="L.met" required class="workoutFormItem--compact workoutFormItem--met">
        <workout-number-stepper
          v-model="local.met"
          :min="0.1"
          :max="30"
          :step="0.1"
          :precision="1"
          :aria-label="L.met"
        />
      </el-form-item>
      <el-form-item :label="L.difficulty" required class="workoutFormItem--compact workoutFormItem--difficulty">
        <el-select v-model="local.difficulty">
          <el-option
            v-for="opt in difficultyOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item :label="L.videoLoop" required class="workoutFormItem--loop">
        <workout-bool-choice
          v-model="local.video_loop"
          false-label="false"
          true-label="true"
          :aria-label="L.videoLoop"
        />
      </el-form-item>
      <p class="workoutMetaStrip__footTip">{{ fieldTips.met }}　{{ fieldTips.videoLoop }}</p>
    </div>

    <el-form-item :label="L.description">
      <el-input
        v-model="local.description"
        type="textarea"
        :rows="3"
        :placeholder="descriptionPlaceholder"
      />
    </el-form-item>

    <el-form-item v-if="showVideoDuration" :label="L.videoDurationSeconds">
      <div class="workoutSystemField">
        <span
          class="workoutSystemField__value link-font-mono"
          :class="{ 'workoutSystemField__value--warn': videoDurationWarn }"
        >
          {{ videoDurationDisplay }}
        </span>
        <p class="workoutSystemField__hint">
          由 zip 内主视角 *_action.mp4 解析；侧视角写入 video_side_duration_seconds
        </p>
      </div>
    </el-form-item>
  </div>
</template>

<script>
import basicFormMixin from './basicFormMixin'
import WorkoutBoolChoice from './WorkoutBoolChoice.vue'
import WorkoutNumberStepper from './WorkoutNumberStepper.vue'
import {
  WORKOUT_FIELD_LABELS,
  WORKOUT_DIFFICULTY_OPTIONS,
  WORKOUT_DESCRIPTION_PLACEHOLDERS,
  WORKOUT_FIELD_TIPS,
  normalizeWorkoutDifficulty,
} from './workoutFormConstants'
import { formatVideoDurationSeconds } from './workoutBindingUtils'
import './workoutFormLayout.css'

const EMPTY = () => ({
  display_name: '',
  difficulty: '',
  description: '',
  video_loop: false,
  met: null,
})

/**
 * MET 为正数（支持 1 位小数）。编辑态兼容旧字段 calories。
 */
function parseMet(value) {
  const parsed = Number.parseFloat(value)
  if (!Number.isFinite(parsed) || parsed < 0.1) {
    return null
  }
  return Math.round(parsed * 10) / 10
}

/** 旧资源仅有 calories 时，临时当 MET 回显（保存后写入 met 并清掉 calories）。 */
function resolveMetFromExta(data) {
  const fromMet = parseMet(data && data.met)
  if (fromMet != null) {
    return fromMet
  }
  return parseMet(data && data.calories)
}

export default {
  name: 'WorkoutActionBasicForm',
  components: { WorkoutBoolChoice, WorkoutNumberStepper },
  mixins: [basicFormMixin],
  data() {
    return {
      local: EMPTY(),
      L: WORKOUT_FIELD_LABELS,
      fieldTips: WORKOUT_FIELD_TIPS,
      videoDurationSeconds: null,
    }
  },
  computed: {
    difficultyOptions() {
      return WORKOUT_DIFFICULTY_OPTIONS
    },
    descriptionPlaceholder() {
      return WORKOUT_DESCRIPTION_PLACEHOLDERS.workout_action
    },
    showVideoDuration() {
      return this.videoDurationSeconds != null
    },
    videoDurationWarn() {
      const parsed = Number.parseFloat(this.videoDurationSeconds)
      return !Number.isFinite(parsed) || parsed <= 0
    },
    videoDurationDisplay() {
      if (this.videoDurationSeconds == null) {
        return '—'
      }
      const parsed = Number.parseFloat(this.videoDurationSeconds)
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return '未能解析（请重新上传 zip 或检查 action.mp4）'
      }
      return formatVideoDurationSeconds(parsed)
    },
  },
  methods: {
    buildExtaPayload() {
      return {
        display_name: this.local.display_name,
        difficulty: this.local.difficulty,
        description: this.local.description,
        video_loop: this.local.video_loop,
        met: parseMet(this.local.met),
      }
    },
    loadFromExta(exta) {
      const data = exta || {}
      this.local = {
        ...EMPTY(),
        display_name: data.display_name || '',
        difficulty: normalizeWorkoutDifficulty(data.difficulty),
        description: data.description || '',
        video_loop: data.video_loop,
        met: resolveMetFromExta(data),
      }
      if (typeof this.local.video_loop !== 'boolean') {
        this.local.video_loop = Boolean(this.local.video_loop)
      }
      this.videoDurationSeconds =
        data.video_duration_seconds != null ? data.video_duration_seconds : null
    },
    reset() {
      this.local = EMPTY()
      this.videoDurationSeconds = null
    },
    validate() {
      if (!String(this.local.display_name || '').trim()) {
        return Promise.reject(`请填写 ${WORKOUT_FIELD_LABELS.displayName}`)
      }
      if (!this.local.difficulty) {
        return Promise.reject(`请选择 ${WORKOUT_FIELD_LABELS.difficulty}`)
      }
      if (parseMet(this.local.met) == null) {
        return Promise.reject(`请填写 ${WORKOUT_FIELD_LABELS.met}（≥ 0.1）`)
      }
      if (typeof this.local.video_loop !== 'boolean') {
        return Promise.reject(`请选择 ${WORKOUT_FIELD_LABELS.videoLoop}`)
      }
      return Promise.resolve()
    },
    captureSnapshot() {
      return { ...this.local }
    },
  },
}
</script>

<style scoped>
.workoutSystemField__value {
  font-size: var(--link-text-sm);
  color: var(--link-ink-secondary);
}

.workoutSystemField__value--warn {
  color: #c45656;
  font-weight: var(--link-font-semibold);
}

.workoutSystemField__hint {
  margin: 6px 0 0;
  font-size: 11px;
  line-height: 1.45;
  color: var(--link-text-muted, #6b6560);
}
</style>
